import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { useViewCoinvestTeaserDocument } from "@vega-api/generated/coinvest-deal-teaser-docs/coinvest-deal-teaser-docs";
import { viewCoinvestNdaDocument } from "@vega-api/generated/coinvest-ndas-upload/coinvest-ndas-upload";
import { CoinvestDocumentType } from "@vega-api/generated/types/coinvestDocumentType";
import { type DealOpportunityLpViewResponseV3 } from "@vega-api/generated/types/dealOpportunityLpViewResponseV3";
import { PDFViewer } from "@vega-core/components/PDFViewer/PDFViewer";
import { m } from "@vega-core/paraglide/messages";
import { downloadUrlAsFile } from "@vega-core/utils/download.helpers";
import { trackCoInvestNdaDownloaded } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useOrganization } from "@vega-modules/hooks/useOrganization";

import { CustomMetrics } from "../components/CustomMetrics";
import { CustomSections } from "../components/CustomSections";
import { DocumentsSection } from "../components/DocumentsSection";
import { OverviewTableOfContents } from "../components/OverviewTableOfContents";
import { useNdaPdfViewer } from "../hooks/useNdaPdfViewer";

type OverviewTabProps = {
  data?: DealOpportunityLpViewResponseV3;
  isLoading?: boolean;
};

export const OverviewTab = ({ data, isLoading }: OverviewTabProps) => {
  const { trackEvent } = useAnalytics();
  const { selectedOrgId: limitedPartnerId } = useOrganization();
  const hasRejectedOffer = data?.isDealOpportunityRejected ?? false;

  const {
    pdfViewer,
    presignedUrl,
    isLoadingPresignedUrl,
    currentDocumentId,
    currentDocumentFilename,
    handleViewDocument,
    getHeaderActions,
  } = useNdaPdfViewer({
    data,
    dealOpportunityId: data?.dealOpportunity.id || "",
    hasRejectedOffer,
  });

  const { refetch: fetchTeaserUrl } = useViewCoinvestTeaserDocument(
    data?.teaser?.documentMetaData.documentId ?? "",
    {
      query: {
        enabled: false,
      },
    },
  );

  const documents = [];

  if (data?.teaser?.documentMetaData.documentId) {
    const nameOfDocument = data.teaser.documentMetaData.nameOfDocument;
    const documentId = data.teaser.documentMetaData.documentId;
    const filename = nameOfDocument || m.coInvestments_teaserDocument();
    documents.push({
      title: m.coInvestments_dealReview_teaser(),
      filename,
      onViewDocument: () => handleViewDocument(documentId, true, filename),
      onDownloadDocument: async () => {
        const result = await fetchTeaserUrl();
        const url = result.data?.data?.presignedUrl?.url;

        if (url) {
          await downloadUrlAsFile(url, filename);
        }
      },
      dataTestId: "view-teaser-button",
    });
  }

  if (data?.ndaStatuses?.documents && data.ndaStatuses.documents.length > 0) {
    data.ndaStatuses.documents.forEach((doc) => {
      const documentId = doc.documentId;
      const nameOfDocument = doc.nameOfDocument;
      const typeOfDocument = doc.typeOfDocument;

      // Skip if documentId is missing
      if (!documentId) return;

      const filename = nameOfDocument || m.coInvestments_ndaDocument();

      const documentItem: {
        title: string;
        filename: string;
        onViewDocument?: () => void;
        onDownloadDocument: () => Promise<void>;
        dataTestId: string;
      } = {
        title: m.coInvestments_dealReview_nda(),
        filename,
        onDownloadDocument: async () => {
          // Use the generated API function directly (not the hook)
          const result = await viewCoinvestNdaDocument(documentId);
          const url = result.data?.presignedUrl?.url;

          if (url) {
            await downloadUrlAsFile(url, filename);
            trackEvent(AnalyticsEvent.COINVEST_NDA_DOWNLOADED, undefined, {
              documentId,
              dealId: data.dealOpportunity.dealId,
              dealOpportunityId: data.dealOpportunity.id,
              limitedPartnerId,
            });
            trackCoInvestNdaDownloaded({
              dealOpportunityId: data.dealOpportunity.id,
              dealId: data.dealOpportunity.dealId,
            });
          }
        },
        dataTestId:
          typeOfDocument === CoinvestDocumentType.PDF
            ? "view-nda-pdf-button"
            : "view-nda-word-button",
      };

      // Only add onViewDocument for PDF files
      if (typeOfDocument === CoinvestDocumentType.PDF) {
        documentItem.onViewDocument = () =>
          handleViewDocument(documentId, false, filename);
      }

      documents.push(documentItem);
    });
  }

  return (
    <div className="mx-auto" data-testid="overview-tab">
      <div className="lg:flex md:gap-10 lg:gap-14 mb-8 lg:items-start">
        <div className="lg:w-64 lg:flex-shrink-0">
          <OverviewTableOfContents
            hasDocuments={documents.length > 0}
            hasKeyMetrics={!!data?.dealOverview.metrics?.length}
            isLoading={isLoading}
            sections={data?.dealOverview.sections}
          />
        </div>
        <div className="lg:flex-1 space-y-4 md:space-y-6 lg:space-y-8 max-w-4xl">
          <CustomMetrics
            dealCurrency={data?.deal.currency}
            metrics={data?.dealOverview.metrics}
          />
          <CustomSections sections={data?.dealOverview.sections} />
          <DocumentsSection documents={documents} />
        </div>
      </div>
      <PDFViewer
        blobOrString={presignedUrl}
        downloadFileName={currentDocumentFilename}
        headerActions={getHeaderActions()}
        isDownloadable
        isLoading={isLoadingPresignedUrl}
        isOpen={pdfViewer.isOpen}
        isPageMapVisible={false}
        isScrollable
        previewDocumentId={currentDocumentId ?? undefined}
        setIsOpen={pdfViewer.setIsOpen}
        showDocumentDropdown={false}
      />
    </div>
  );
};
